import { PropsWithChildren, createContext, useContext, useState } from "react";

import { useMountEffect } from "@/hooks/useMountEffect";

import type { Stripe } from "@stripe/stripe-js";

const StripeContext = createContext<Stripe | null>(null);

export function useStripeObject() {
  const ctx = useContext(StripeContext);
  if (!ctx) {
    throw new Error("StripeProvider not found");
  }
  return ctx;
}

export function StripeProvider(props: PropsWithChildren) {
  const [stripe, setStripe] = useState<Stripe | null>(null);

  useMountEffect(async () => {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      // eslint-disable-next-line no-console
      console.error("VITE_STRIPE_PUBLISHABLE_KEY not set");
      return;
    }
    try {
      // Imported here rather than at module scope for two reasons. `/pure`,
      // because the package root injects Stripe's script tag as an import-time
      // side effect. Dynamically, because the billing barrel is reached
      // eagerly from routes.tsx and `@stripe/stripe-js` ships no
      // `sideEffects: false`, so a static import cannot be tree-shaken out of
      // the entry chunk even where nothing mounts this provider.
      const { loadStripe } = await import("@stripe/stripe-js/pure");
      const stripe = await loadStripe(publishableKey);
      setStripe(stripe);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error loading stripe", err);
      return;
    }
  });

  if (!stripe) {
    return null;
  }

  return (
    <StripeContext.Provider value={stripe}>
      {props.children}
    </StripeContext.Provider>
  );
}
